import type {
  SavedViewGroupByT,
  SavedViewRule,
  SavedViewSort,
  SmartList,
  SortDirectionT,
} from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import { type WithNow, getDeletedMap, getSmartListsMap } from './doc.ts';

export interface CreateSmartListInput {
  name: string;
  query: string;
  ruleTree?: SavedViewRule;
  groupBy?: SavedViewGroupByT;
  groupDirection?: SortDirectionT;
  sort?: SavedViewSort[];
  showCompleted?: boolean;
  showTrashed?: boolean;
}

export interface UpdateSmartListInput {
  name?: string;
  query?: string;
  ruleTree?: SavedViewRule;
  groupBy?: SavedViewGroupByT;
  groupDirection?: SortDirectionT;
  sort?: SavedViewSort[];
  showCompleted?: boolean;
  showTrashed?: boolean;
  order?: number;
}

function ts(options: WithNow): number {
  return options.now ?? Date.now();
}

export function listSmartLists(doc: Y.Doc): SmartList[] {
  const out: SmartList[] = [];
  for (const [id, inner] of getSmartListsMap(doc).entries()) {
    out.push(smartListFromMap(id, inner));
  }
  return out.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
}

export function getSmartList(doc: Y.Doc, id: string): SmartList | undefined {
  const inner = getSmartListsMap(doc).get(id);
  return inner ? smartListFromMap(id, inner) : undefined;
}

export function createSmartList(
  doc: Y.Doc,
  input: CreateSmartListInput,
  options: WithNow = {},
): SmartList {
  const now = ts(options);
  const smartLists = getSmartListsMap(doc);
  const id = newId();
  const maxOrder = Math.max(-1, ...listSmartLists(doc).map((smartList) => smartList.order));
  const inner = new Y.Map<unknown>();
  inner.set('name', input.name);
  inner.set('query', input.query);
  if (input.ruleTree !== undefined) inner.set('ruleTree', input.ruleTree);
  inner.set('groupBy', input.groupBy ?? 'project');
  inner.set('groupDirection', input.groupDirection ?? 'asc');
  inner.set('sort', input.sort ?? [{ key: 'due', direction: 'asc' }]);
  inner.set('showCompleted', input.showCompleted ?? true);
  inner.set('showTrashed', input.showTrashed ?? false);
  inner.set('order', maxOrder + 1);
  inner.set('createdAt', now);
  inner.set('updatedAt', now);
  smartLists.set(id, inner);
  return smartListFromMap(id, inner);
}

export function updateSmartList(
  doc: Y.Doc,
  id: string,
  patch: UpdateSmartListInput,
  options: WithNow = {},
): void {
  const inner = getSmartListsMap(doc).get(id);
  if (!inner) return;
  const now = ts(options);
  doc.transact(() => {
    if (patch.name !== undefined) inner.set('name', patch.name);
    if (patch.query !== undefined) inner.set('query', patch.query);
    if (patch.ruleTree !== undefined) inner.set('ruleTree', patch.ruleTree);
    if (patch.groupBy !== undefined) inner.set('groupBy', patch.groupBy);
    if (patch.groupDirection !== undefined) inner.set('groupDirection', patch.groupDirection);
    if (patch.sort !== undefined) inner.set('sort', patch.sort);
    if (patch.showCompleted !== undefined) inner.set('showCompleted', patch.showCompleted);
    if (patch.showTrashed !== undefined) inner.set('showTrashed', patch.showTrashed);
    if (patch.order !== undefined) inner.set('order', patch.order);
    inner.set('updatedAt', now);
  });
}

export function deleteSmartList(doc: Y.Doc, id: string, options: WithNow = {}): void {
  const smartLists = getSmartListsMap(doc);
  if (!smartLists.has(id)) return;
  const now = ts(options);
  doc.transact(() => {
    smartLists.delete(id);
    getDeletedMap(doc).set(id, now);
  });
}

function smartListFromMap(id: string, inner: Y.Map<unknown>): SmartList {
  const rawSort = inner.get('sort');
  const sort = Array.isArray(rawSort)
    ? (rawSort as SavedViewSort[]).filter(
        (item) =>
          item &&
          typeof item === 'object' &&
          typeof item.key === 'string' &&
          (item.direction === 'asc' || item.direction === 'desc'),
      )
    : [{ key: 'due', direction: 'asc' } as SavedViewSort];
  const groupDirection = inner.get('groupDirection');
  const groupBy = inner.get('groupBy');
  return {
    id,
    name: String(inner.get('name') ?? ''),
    query: String(inner.get('query') ?? ''),
    ruleTree:
      typeof inner.get('ruleTree') === 'object'
        ? (inner.get('ruleTree') as SavedViewRule)
        : undefined,
    groupBy:
      typeof groupBy === 'string' &&
      [
        'none',
        'project',
        'tag',
        'priority',
        'due',
        'scheduled',
        'workflow',
        'estimate',
        'owner',
      ].includes(groupBy)
        ? (groupBy as SavedViewGroupByT)
        : 'project',
    groupDirection: groupDirection === 'desc' ? 'desc' : 'asc',
    sort,
    showCompleted: inner.get('showCompleted') !== false,
    showTrashed: inner.get('showTrashed') === true,
    order: Number(inner.get('order') ?? 0),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };
}
